import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import type { PlatformRoleCode, RoleCode } from '@cattle/money'
import type {
  MeResponse,
  PlatformMeResponse,
  PlatformSessionResponse,
  SessionResponse,
} from '@cattle/contracts'

import { ApiRequestError, get, post } from '../lib/apiClient'

/**
 * WHO IS SIGNED IN — for real, from the server.
 *
 * Two sign-ins exist and they are deliberately different doors. The Owner
 * Console signs in at `POST /auth/login` with a tenant slug; the Platform
 * Console signs in at `POST /platform/auth/login`, which is checked against
 * `tenant_id IS NULL` only. The trader route REFUSES a platform account with
 * "This account signs in through the platform console." rather than letting a
 * slug guess authenticate a platform user — see `controllers/auth.controller.ts`.
 *
 * Both are COOKIE sessions: `chooseTransport()` picks the cookie whenever the
 * request carries an `Origin`, which a browser always sends on a POST. The
 * token never reaches page script, so there is nothing here to store — except
 * one thing, noted below.
 *
 * Roles are enforced server-side. Everything in this file is only what the
 * client needs in order not to render a destination the API would refuse.
 */

export interface Session {
  platformRole: PlatformRoleCode
  platformUserName: string
  /** The role the signed-in person has inside the trader's own book. */
  bookRole: RoleCode
  bookUserName: string
  businessName: string
}

/** Which console the current URL belongs to. `/admin` is the trader's own. */
export type Surface = 'platform' | 'owner'

interface SessionState {
  surface: Surface
  /** Resolved identity, or `null` when nobody is signed in to this surface. */
  session: Session | null
  /** The trader payload. Absent in the platform console — there is no book. */
  me: MeResponse | null
  platformMe: PlatformMeResponse | null
  isPending: boolean
  signInPlatform: (username: string, password: string) => Promise<void>
  signInOwner: (tenantSlug: string, username: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const SessionContext = createContext<SessionState | null>(null)

/**
 * The one thing kept in the browser: the platform console's own identity.
 *
 * There is **no `GET /platform/me`** in the contract — P0 returns `me` inside
 * the login response and nothing serves it afterwards — so a page reload has no
 * way to ask the server who it is talking to. The name and role are cached here
 * and re-validated on boot against a route the cookie must still satisfy; the
 * cookie remains the only credential, and the role is only ever used to decide
 * what to draw. See the report: this wants a `GET /platform/me`.
 */
const PLATFORM_ME_KEY = 'cattlebook.platformMe'

function readCachedPlatformMe(): PlatformMeResponse | null {
  try {
    const raw = window.sessionStorage.getItem(PLATFORM_ME_KEY)
    return raw ? (JSON.parse(raw) as PlatformMeResponse) : null
  } catch {
    return null
  }
}

function writeCachedPlatformMe(value: PlatformMeResponse | null): void {
  try {
    if (value) window.sessionStorage.setItem(PLATFORM_ME_KEY, JSON.stringify(value))
    else window.sessionStorage.removeItem(PLATFORM_ME_KEY)
  } catch {
    /* a private window with storage disabled still signs in; it just re-asks. */
  }
}

/**
 * Whether an error proves this cookie is not a live platform session.
 *
 * `UNAUTHENTICATED` is the expired session. `FORBIDDEN` is the OTHER way this
 * goes wrong and the one that bites: the two consoles share one `cb_session`
 * cookie, so signing in to the Owner Console leaves a trader session behind a
 * cached platform identity, and `authorizePlatform` answers that with 403
 * ("This is the platform console. Sign in with a platform account."). Treating
 * only 401 as proof leaves a trader looking at the platform console's shell.
 *
 * `MAINTENANCE_MODE` is deliberately NOT in this set, though it is also a 403:
 * it must not sign the platform owner out of the console that turns it off.
 */
const isNotAPlatformSession = (error: unknown): boolean =>
  error instanceof ApiRequestError &&
  (error.code === 'UNAUTHENTICATED' || error.code === 'FORBIDDEN')

export function surfaceFor(pathname: string): Surface {
  return pathname === '/admin' || pathname.startsWith('/admin/') ? 'owner' : 'platform'
}

/**
 * Boot. One query per surface, and only the surface being looked at runs —
 * the two consoles share one `cb_session` cookie, so probing both would sign
 * one of them out of the other's session for no reason.
 */
function usePlatformSession(enabled: boolean): UseQueryResult<PlatformMeResponse | null> {
  return useQuery<PlatformMeResponse | null>({
    queryKey: ['session', 'platform'],
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const cached = readCachedPlatformMe()
      if (!cached) return null
      try {
        // Not "is the cache fresh" — "is the cookie still a platform session".
        // `platform.read` is the capability both platform roles hold.
        await get('/platform/settings')
        return cached
      } catch (error) {
        if (isNotAPlatformSession(error)) {
          writeCachedPlatformMe(null)
          return null
        }
        return cached
      }
    },
  })
}

function useOwnerSession(enabled: boolean): UseQueryResult<MeResponse | null> {
  return useQuery<MeResponse | null>({
    queryKey: ['session', 'owner'],
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      try {
        return await get<MeResponse>('/me')
      } catch (error) {
        // A platform session reaching `/me` is refused, not unauthenticated —
        // it is a real session, just not one that belongs to a book.
        if (isNotAPlatformSession(error)) return null
        throw error
      }
    },
  })
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const surface = surfaceFor(pathname)
  const queryClient = useQueryClient()

  const platform = usePlatformSession(surface === 'platform')
  const owner = useOwnerSession(surface === 'owner')

  const signInPlatform = useCallback(
    async (username: string, password: string) => {
      const result = await post<PlatformSessionResponse>('/platform/auth/login', {
        username,
        password,
      })
      writeCachedPlatformMe(result.me)
      queryClient.setQueryData(['session', 'platform'], result.me)
      await queryClient.invalidateQueries()
    },
    [queryClient],
  )

  const signInOwner = useCallback(
    async (tenantSlug: string, username: string, password: string) => {
      const result = await post<SessionResponse>('/auth/login', {
        tenantSlug,
        username,
        password,
      })
      // One cookie, two consoles: this sign-in replaced whatever platform
      // session was here, so the cached platform identity is now a lie.
      writeCachedPlatformMe(null)
      queryClient.setQueryData(['session', 'platform'], null)
      queryClient.setQueryData(['session', 'owner'], result.me)
      await queryClient.invalidateQueries()
    },
    [queryClient],
  )

  const signOut = useCallback(async () => {
    try {
      await post('/auth/logout')
    } finally {
      writeCachedPlatformMe(null)
      queryClient.setQueryData(['session', 'platform'], null)
      queryClient.setQueryData(['session', 'owner'], null)
      queryClient.clear()
    }
  }, [queryClient])

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['session'] })
  }, [queryClient])

  const value = useMemo<SessionState>(() => {
    const platformMe = surface === 'platform' ? (platform.data ?? null) : null
    const me = surface === 'owner' ? (owner.data ?? null) : null

    const session: Session | null = platformMe
      ? {
          platformRole: platformMe.user.role,
          platformUserName: platformMe.user.name,
          // A platform actor has no role inside anyone's book until a P5 grant
          // is opened, and the grant is not the console's own session.
          bookRole: 'OWNER',
          bookUserName: platformMe.user.name,
          businessName: '',
        }
      : me
        ? {
            // Never `PLATFORM_OWNER` for a trader: a tenant OWNER and a
            // PLATFORM_OWNER are deliberately not the same type anywhere here.
            platformRole: 'PLATFORM_SUPPORT',
            platformUserName: me.user.name,
            bookRole: me.user.role,
            bookUserName: me.user.name,
            businessName: me.tenant.name,
          }
        : null

    return {
      surface,
      session,
      me,
      platformMe,
      isPending: surface === 'platform' ? platform.isPending : owner.isPending,
      signInPlatform,
      signInOwner,
      signOut,
      refresh,
    }
  }, [
    surface,
    platform.data,
    platform.isPending,
    owner.data,
    owner.isPending,
    signInPlatform,
    signInOwner,
    signOut,
    refresh,
  ])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSessionState(): SessionState {
  const state = useContext(SessionContext)
  if (!state) throw new Error('useSessionState outside SessionProvider')
  return state
}

export function useSession(): Session {
  const { session } = useSessionState()
  if (!session) throw new Error('useSession before sign-in')
  return session
}

/**
 * True for the platform owner ONLY. Suspension, deletion and pricing.
 *
 * A trader signed into his own console is never this, whatever his role in his
 * own book — which is why it reads `platformMe` rather than `session`.
 */
export function useIsPlatformOwner(): boolean {
  const { platformMe } = useSessionState()
  return platformMe?.user.role === 'PLATFORM_OWNER'
}

/** The server's day in Asia/Karachi. Never `new Date()` — see eslint rule (b). */
export function useServerToday(): string | undefined {
  const { me, platformMe } = useSessionState()
  return me?.today ?? platformMe?.today
}
